import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useSeo } from "@/hooks/useSeo";
import { MATERIALS } from "@/data/site";
import { Upload, FileCheck, X, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPT = ".pdf,.step,.stp,.dxf,.dwg,.iges,.igs,.zip,.jpg,.jpeg,.png";

const field = "w-full border border-neutral-800 bg-neutral-900 px-4 py-3 text-white placeholder-neutral-600 outline-none transition-colors focus:border-orange-500";
const labelCls = "mb-1.5 block text-sm font-medium text-neutral-300";

export default function RequestQuote() {
  useSeo(
    "Request a Quote | CNC Machining RFQ | Edgeworks Manufacturing",
    "Request a CNC machining quote from Edgeworks Manufacturing. Upload a drawing or model (PDF, STEP, DXF, DWG, IGES) with material, quantity, and required date."
  );

  const empty = {
    name: "", company: "", email: "", phone: "", part_name: "",
    quantity: "", material: "", required_date: "", notes: "",
  };
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f && f.size > 25 * 1024 * 1024) {
      toast.error("File is larger than 25MB. Please send a smaller file or a ZIP.");
      return;
    }
    setFile(f || null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      data.append("consent", "true");
      if (file) data.append("file", file);
      await axios.post(`${API}/quote`, data, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success("RFQ submitted. I'll review it and get back to you.");
      setForm(empty);
      setFile(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <section className="mx-auto max-w-2xl px-4 py-28 text-center sm:px-6 lg:px-8">
        <CheckCircle2 className="mx-auto text-orange-500" size={56} />
        <h1 className="mt-6 font-heading text-3xl font-black tracking-tight text-white sm:text-4xl">
          Thanks — your RFQ is in
        </h1>
        <p className="mt-4 text-neutral-400">
          Your request has been received. I'll review the details and any uploaded files and follow up
          by email at the address you provided.
        </p>
        <button
          onClick={() => setDone(false)}
          data-testid="submit-another-btn"
          className="mt-8 inline-flex items-center gap-2 border border-neutral-700 px-6 py-3 font-semibold text-white transition-colors hover:border-neutral-500 hover:bg-neutral-900"
        >
          Submit another RFQ
        </button>
      </section>
    );
  }

  return (
    <div>
      <section className="border-b border-neutral-800 bg-neutral-900">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-orange-500">Request a Quote</p>
          <h1 className="mt-3 font-heading text-4xl font-black tracking-tighter text-white sm:text-5xl">
            Request a CNC Machining Quote
          </h1>
          <p className="mt-4 max-w-2xl text-neutral-300">
            The fastest way to get a quote is to include a drawing, material, quantity, finish
            requirements, tolerance requirements, and your required delivery date.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <form onSubmit={onSubmit} className="border border-neutral-800 bg-neutral-950 p-6 sm:p-9" data-testid="quote-form">
          <div className="grid gap-6">
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Name *</label>
                <input name="name" required value={form.name} onChange={onChange} className={field} data-testid="quote-name" />
              </div>
              <div>
                <label className={labelCls}>Company</label>
                <input name="company" value={form.company} onChange={onChange} className={field} data-testid="quote-company" />
              </div>
              <div>
                <label className={labelCls}>Email *</label>
                <input type="email" name="email" required value={form.email} onChange={onChange} className={field} data-testid="quote-email" />
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <input name="phone" value={form.phone} onChange={onChange} className={field} data-testid="quote-phone" />
              </div>
              <div>
                <label className={labelCls}>Part name or project name</label>
                <input name="part_name" value={form.part_name} onChange={onChange} className={field} data-testid="quote-part-name" />
              </div>
              <div>
                <label className={labelCls}>Quantity</label>
                <input name="quantity" value={form.quantity} onChange={onChange} className={field} data-testid="quote-quantity" />
              </div>
              <div>
                <label className={labelCls}>Material</label>
                <select name="material" value={form.material} onChange={onChange} className={field} data-testid="quote-material">
                  <option value="">Select a material</option>
                  {MATERIALS.map((m) => <option key={m} value={m}>{m}</option>)}
                  <option value="Other / not sure">Other / not sure</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Required date</label>
                <input type="date" name="required_date" value={form.required_date} onChange={onChange} className={field} data-testid="quote-required-date" />
              </div>
            </div>

            {/* File upload */}
            <div>
              <label className={labelCls}>Upload drawing or model</label>
              <input
                ref={fileRef}
                type="file"
                accept={ACCEPT}
                onChange={onFile}
                className="hidden"
                data-testid="quote-file-input"
              />
              {!file ? (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  data-testid="quote-file-trigger"
                  className="flex w-full flex-col items-center justify-center gap-2 border border-dashed border-neutral-700 bg-neutral-900 px-4 py-9 text-center transition-colors hover:border-orange-500"
                >
                  <Upload className="text-orange-500" size={26} />
                  <span className="text-sm font-medium text-neutral-200">Click to upload a file</span>
                  <span className="font-mono text-xs text-neutral-500">PDF, STEP, STP, DXF, DWG, IGES, ZIP, JPG, PNG · max 25MB</span>
                </button>
              ) : (
                <div className="flex items-center justify-between border border-neutral-700 bg-neutral-900 px-4 py-3" data-testid="quote-file-selected">
                  <span className="flex min-w-0 items-center gap-2 text-sm text-neutral-200">
                    <FileCheck className="shrink-0 text-orange-500" size={18} />
                    <span className="truncate">{file.name}</span>
                  </span>
                  <button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-neutral-400 hover:text-white" data-testid="quote-file-remove">
                    <X size={18} />
                  </button>
                </div>
              )}
              <p className="mt-3 text-xs leading-relaxed text-neutral-500" data-testid="quote-privacy-note">
                Uploaded drawings, models, and project details will be used only for quoting,
                manufacturing review, and project communication. Customer files will not be shared
                publicly or used for marketing without permission.
              </p>
            </div>

            <div>
              <label className={labelCls}>Notes / project details</label>
              <textarea name="notes" rows={5} value={form.notes} onChange={onChange} className={field}
                placeholder="Finish requirements, tolerances, quantities, deadlines, or anything else that helps."
                data-testid="quote-notes" />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="quote-submit"
              className="inline-flex items-center justify-center gap-2 bg-orange-500 px-6 py-4 font-bold text-white transition-colors hover:bg-orange-600 disabled:opacity-60"
            >
              {loading ? <><Loader2 className="animate-spin" size={18} /> Submitting…</> : "Submit RFQ"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
